import React, { useState, useEffect } from 'react';
import { Search, Filter, Star, Users, TrendingUp, Clock, Tag, Globe, Plus, CheckCircle, Sparkles, Target, Brain, Flame, Eye, Calendar, RefreshCw } from 'lucide-react';
import { readerService } from '../../services/readerService';

interface Newsletter {
  id: string;
  title: string;
  author: string;
  description: string;
  category: string;
  subscriberCount: number;
  avgRating: number;
  postCount: number;
  frequency: string;
  lastPost: Date;
  tags: string[];
  avatar?: string;
  color: string;
  reason?: string;
  isRecommended: boolean;
  isTrending: boolean;
  isNew: boolean;
  isSubscribed: boolean;
  engagementScore: number;
  qualityScore: number;
  readingTime: number;
  language: string;
  price: 'free' | 'premium' | 'freemium';
  priceAmount?: number;
}

interface Category {
  id: string;
  name: string;
  icon: string;
  color: string;
  count: number;
}

interface SmartDiscoverySystemProps {
  userId: string;
  onSubscribe: (newsletterId: string) => void;
  onPreview: (newsletterId: string) => void;
  onViewProfile: (authorId: string) => void;
}

type DiscoveryMode = 'recommended' | 'trending' | 'new' | 'similar' | 'category';
type SortBy = 'relevance' | 'subscribers' | 'rating' | 'recent' | 'engagement';

const SmartDiscoverySystem: React.FC<SmartDiscoverySystemProps> = ({
  userId,
  onSubscribe,
  onPreview,
  onViewProfile
}) => {
  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [filteredNewsletters, setFilteredNewsletters] = useState<Newsletter[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [discoveryMode, setDiscoveryMode] = useState<DiscoveryMode>('recommended');
  const [sortBy, setSortBy] = useState<SortBy>('relevance');
  const [showFilters, setShowFilters] = useState(false);
  const [priceFilter, setPriceFilter] = useState<'all' | 'free' | 'paid'>('all');
  const [languageFilter, setLanguageFilter] = useState<string>('all');
  const [minRating, setMinRating] = useState(0);
  const [maxSubscribers, setMaxSubscribers] = useState(1000000);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Load real data from reader service
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [newsletters, categories] = await Promise.all([
          readerService.getRecommendedNewsletters(userId, 20),
          readerService.getCategories()
        ]);
        
        setNewsletters(newsletters);
        setCategories(categories);
      } catch (error) {
        console.error('Error loading discovery data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [userId]);

  // Fallback mock data for development
  useEffect(() => {
    if (newsletters.length === 0) {
      const mockNewsletters: Newsletter[] = [
        {
          id: '1',
          title: 'The Future of AI',
          author: 'Dr. Sarah Chen',
          description: 'Weekly insights on artificial intelligence, machine learning, and the future of technology.',
          category: 'Technology',
          subscriberCount: 125000,
          avgRating: 4.8,
          postCount: 156,
          frequency: 'Weekly',
          lastPost: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
          tags: ['AI', 'Machine Learning', 'Technology', 'Future'],
          color: 'blue',
          reason: 'Based on your interest in technology and AI',
          isRecommended: true,
          isTrending: true,
          isNew: false,
          isSubscribed: false,
          engagementScore: 92,
          qualityScore: 95,
          readingTime: 8,
          language: 'English',
          price: 'free'
        },
        {
          id: '2',
          title: 'Design Systems Weekly',
          author: 'Emma Wilson',
          description: 'The latest in design systems, UI/UX trends, and design tools for modern teams.',
          category: 'Design',
          subscriberCount: 89000,
          avgRating: 4.6,
          postCount: 203,
          frequency: 'Weekly',
          lastPost: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
          tags: ['Design', 'UI/UX', 'Design Systems', 'Tools'],
          color: 'purple',
          reason: 'Similar to newsletters you read',
          isRecommended: true,
          isTrending: false,
          isNew: false,
          isSubscribed: true,
          engagementScore: 88,
          qualityScore: 92,
          readingTime: 6,
          language: 'English',
          price: 'free'
        },
        {
          id: '3',
          title: 'Startup Insights',
          author: 'Mike Johnson',
          description: 'Weekly analysis of startup trends, funding rounds, and entrepreneurial insights.',
          category: 'Business',
          subscriberCount: 67000,
          avgRating: 4.4,
          postCount: 89,
          frequency: 'Bi-weekly',
          lastPost: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
          tags: ['Startups', 'Business', 'Entrepreneurship', 'Funding'],
          color: 'green',
          reason: 'Trending in your network',
          isRecommended: false,
          isTrending: true,
          isNew: false,
          isSubscribed: false,
          engagementScore: 85,
          qualityScore: 88,
          readingTime: 10,
          language: 'English',
          price: 'premium',
          priceAmount: 5
        }
      ];

    const mockCategories: Category[] = [
      { id: 'technology', name: 'Technology', icon: 'zap', color: 'blue', count: 245 },
      { id: 'design', name: 'Design', icon: 'palette', color: 'purple', count: 189 },
      { id: 'business', name: 'Business', icon: 'trending-up', color: 'green', count: 156 },
      { id: 'science', name: 'Science', icon: 'brain', color: 'indigo', count: 98 },
      { id: 'lifestyle', name: 'Lifestyle', icon: 'heart', color: 'pink', count: 203 },
      { id: 'news', name: 'News', icon: 'globe', color: 'red', count: 167 }
    ];

      setNewsletters(mockNewsletters);
      setCategories(mockCategories);
      setLoading(false);
    }
  }, [userId]);

  // Filter and sort newsletters
  useEffect(() => {
    let filtered = [...newsletters];

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(newsletter =>
        newsletter.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        newsletter.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
        newsletter.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        newsletter.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(newsletter => newsletter.category.toLowerCase() === selectedCategory);
    }

    // Filter by price
    if (priceFilter !== 'all') {
      filtered = filtered.filter(newsletter => newsletter.price === priceFilter);
    }

    // Filter by language
    if (languageFilter !== 'all') {
      filtered = filtered.filter(newsletter => newsletter.language === languageFilter);
    }

    // Filter by rating
    filtered = filtered.filter(newsletter => newsletter.avgRating >= minRating);

    // Filter by subscriber count
    filtered = filtered.filter(newsletter => newsletter.subscriberCount <= maxSubscribers);

    // Apply discovery mode filters
    switch (discoveryMode) {
      case 'recommended':
        filtered = filtered.filter(newsletter => newsletter.isRecommended);
        break;
      case 'trending':
        filtered = filtered.filter(newsletter => newsletter.isTrending);
        break;
      case 'new':
        filtered = filtered.filter(newsletter => newsletter.isNew);
        break;
      case 'similar':
        // This would be based on user's reading history
        filtered = filtered.filter(newsletter => newsletter.engagementScore > 80);
        break;
    }

    // Sort newsletters
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'relevance':
          return b.engagementScore - a.engagementScore;
        case 'subscribers':
          return b.subscriberCount - a.subscriberCount;
        case 'rating':
          return b.avgRating - a.avgRating;
        case 'recent':
          return b.lastPost.getTime() - a.lastPost.getTime();
        case 'engagement':
          return b.engagementScore - a.engagementScore;
        default:
          return 0;
      }
    });

    setFilteredNewsletters(filtered);
  }, [newsletters, searchQuery, selectedCategory, discoveryMode, sortBy, priceFilter, languageFilter, minRating, maxSubscribers]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsRefreshing(false);
  };

  const formatSubscriberCount = (count: number) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const getCategoryIcon = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.icon || Globe;
  };

  const getCategoryColor = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.color || 'gray';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Discover Newsletters</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Find your next favorite newsletter with AI-powered recommendations
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Discovery Modes */}
      <div className="flex flex-wrap gap-2">
        {[
          { id: 'recommended', label: 'Recommended', icon: Sparkles, color: 'blue' },
          { id: 'trending', label: 'Trending', icon: TrendingUp, color: 'red' },
          { id: 'new', label: 'New', icon: Star, color: 'green' },
          { id: 'similar', label: 'Similar', icon: Target, color: 'purple' },
          { id: 'category', label: 'By Category', icon: Tag, color: 'indigo' }
        ].map((mode) => (
          <button
            key={mode.id}
            onClick={() => setDiscoveryMode(mode.id as DiscoveryMode)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              discoveryMode === mode.id
                ? `bg-${mode.color}-100 text-${mode.color}-600 dark:bg-${mode.color}-900 dark:text-${mode.color}-400`
                : 'hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            <mode.icon className="w-4 h-4" />
            {mode.label}
          </button>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search newsletters, authors, or topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name} ({category.count})
              </option>
            ))}
          </select>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortBy)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="relevance">Most Relevant</option>
            <option value="subscribers">Most Subscribers</option>
            <option value="rating">Highest Rated</option>
            <option value="recent">Most Recent</option>
            <option value="engagement">Most Engaging</option>
          </select>

          {/* Advanced Filters */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
        </div>

        {/* Advanced Filters Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Price</label>
                <select
                  value={priceFilter}
                  onChange={(e) => setPriceFilter(e.target.value as 'all' | 'free' | 'paid')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="all">All Prices</option>
                  <option value="free">Free</option>
                  <option value="paid">Paid</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Language</label>
                <select
                  value={languageFilter}
                  onChange={(e) => setLanguageFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="all">All Languages</option>
                  <option value="English">English</option>
                  <option value="Spanish">Spanish</option>
                  <option value="French">French</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Min Rating</label>
                <input
                  type="range"
                  min="0"
                  max="5"
                  step="0.1"
                  value={minRating}
                  onChange={(e) => setMinRating(Number(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>0</span>
                  <span>{minRating.toFixed(1)}</span>
                  <span>5.0</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Newsletter Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredNewsletters.map((newsletter) => {
          const CategoryIcon = getCategoryIcon(newsletter.category.toLowerCase());
          const categoryColor = getCategoryColor(newsletter.category.toLowerCase());

          return (
            <div
              key={newsletter.id}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all duration-200"
            >
              {/* Header */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-lg bg-${newsletter.color}-100 dark:bg-${newsletter.color}-900 flex items-center justify-center`}>
                      <CategoryIcon className={`w-6 h-6 text-${newsletter.color}-600 dark:text-${newsletter.color}-400`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100 line-clamp-1">
                        {newsletter.title}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        by {newsletter.author}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {newsletter.isRecommended && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400 text-xs rounded-full">
                        <Sparkles className="w-3 h-3 inline mr-1" />
                        Recommended
                      </span>
                    )}
                    {newsletter.isTrending && (
                      <span className="px-2 py-1 bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400 text-xs rounded-full">
                        <Flame className="w-3 h-3 inline mr-1" />
                        Trending
                      </span>
                    )}
                    {newsletter.isNew && (
                      <span className="px-2 py-1 bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400 text-xs rounded-full">
                        <Star className="w-3 h-3 inline mr-1" />
                        New
                      </span>
                    )}
                  </div>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                  {newsletter.description}
                </p>

                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {formatSubscriberCount(newsletter.subscriberCount)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Star className="w-3 h-3" />
                    {newsletter.avgRating.toFixed(1)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {newsletter.readingTime} min
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {newsletter.frequency}
                  </span>
                </div>
              </div>

              {/* Tags */}
              <div className="p-4">
                <div className="flex flex-wrap gap-1 mb-4">
                  {newsletter.tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded"
                    >
                      {tag}
                    </span>
                  ))}
                  {newsletter.tags.length > 3 && (
                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded">
                      +{newsletter.tags.length - 3}
                    </span>
                  )}
                </div>

                {/* Reason */}
                <div className="mb-4 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
                    <Brain className="w-4 h-4" />
                    <span>{newsletter.reason}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onSubscribe(newsletter.id)}
                    disabled={newsletter.isSubscribed}
                    className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                      newsletter.isSubscribed
                        ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {newsletter.isSubscribed ? (
                      <>
                        <CheckCircle className="w-4 h-4 inline mr-2" />
                        Subscribed
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 inline mr-2" />
                        Subscribe
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => onPreview(newsletter.id)}
                    className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onViewProfile(newsletter.author)}
                    className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Globe className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredNewsletters.length === 0 && (
        <div className="text-center py-12">
          <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No newsletters found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Try adjusting your search terms or filters to find more newsletters.
          </p>
        </div>
      )}
    </div>
  );
};

export default SmartDiscoverySystem;
